import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Group, Stack, TextInput } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { api, setOnboardingAccessToken } from "@utils/api";

export const ResumeOnboarding = ({ onResumed }: { onResumed: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [usePhone, setUsePhone] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resume = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await api.post("onboarding/continue-by-code", { code: code.trim() });
      setOnboardingAccessToken(response.data.onboarding_access_token);
      onResumed();
    } catch (reason: any) {
      setError(reason?.response?.data?.detail || t("onboarding-resume-error"));
    } finally {
      setLoading(false);
    }
  };

  const resumeByPhone = async () => {
    setError("");
    setLoading(true);
    try {
      if (!otpSent) {
        await api.post("onboarding/resume/request-otp", { phone_number: phone.trim() });
        setOtpSent(true);
      } else {
        const response = await api.post("onboarding/resume/verify-otp", {
          phone_number: phone.trim(), otp_code: otp.trim(),
        });
        setOnboardingAccessToken(response.data.onboarding_access_token);
        onResumed();
      }
    } catch (reason: any) {
      setError(reason?.response?.data?.detail || t("onboarding-resume-error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="md">
      {usePhone ? <>
        <TextInput label={t("onboarding-resume-phone")} value={phone}
          onChange={event => setPhone(event.currentTarget.value)} autoComplete="tel" />
        {otpSent && <TextInput label={t("onboarding-resume-otp")} value={otp}
          onChange={event => setOtp(event.currentTarget.value)} autoComplete="one-time-code" />}
      </> : <TextInput
        label={t("onboarding-resume-code")}
        value={code}
        onChange={event => setCode(event.currentTarget.value)}
        autoComplete="off"
      />}
      {error && <Alert color="red">{error}</Alert>}
      <Button onClick={usePhone ? resumeByPhone : resume}
        disabled={usePhone ? (!phone.trim() || (otpSent && !otp.trim())) : !code.trim()} loading={loading}>
        {usePhone ? (otpSent ? t("onboarding-verify-otp") : t("onboarding-send-otp")) : t("onboarding-resume")}
      </Button>
      <Group justify="space-between">
        <Button variant="subtle" onClick={() => { setUsePhone(!usePhone); setError(""); }}>
          {usePhone ? t("onboarding-use-code") : t("onboarding-use-phone")}
        </Button>
        <Button variant="subtle" onClick={() => navigate("/")}>{t("onboarding-sign-in")}</Button>
      </Group>
    </Stack>
  );
};
